/**
 * Zod schemas for the WorkWise wizard.
 *
 *  - buildFormSchema(ctx): the wizard form. The deployment model picks the
 *    variant: SaaS-Hybrid needs a shared DB and gateway (`sharedServerId`),
 *    Dedicated needs `sku`, `vmName` (and so on) for both. The environment,
 *    customer number and model are passed in so the VM-name rules sit on the
 *    field they belong to instead of a root refinement that zod skips whenever
 *    any other field has a type error. Never validates or echoes a secret
 *    value; only references. Every server's data disks use the same list rules
 *    (R3-G); a dedicated DB needs at least one. No admin credential (revision
 *    6): the VM passwords are generated during deployment.
 *  - payloadSchema: the strict outgoing payload (additionalProperties: false).
 *    The frontend builder is parsed against it, and so is the mock backend.
 *    Like the backend it refuses mixed DB/gateway modes (R3-A), a dedicated
 *    size other than the step-2 size (R3-G), a `network` that does not fit
 *    the model (R4-A: Dedicated needs valid CIDRs, SaaS-Hybrid may only name an
 *    existing network), the retired `appServer.adminAccess` (revision 6) and
 *    the retired `gatewayServer.hostname` / `gatewayServer.tlsCertRef`
 *    (revision 8), all as unknown keys.
 *
 *  A dedicated DB (see utils/workwise/dbStorage.ts) takes SQL Server 2022 only, a SQL authentication
 *  mode ('sql' needs a valid, non-reserved login; no password is ever typed or sent) and, instead of a
 *  free-form disk list, the "SQL Server storage" rows (data, log, TempDB): in the payload they are one
 *  role-tagged `disks` list of at most 16 with at least a data and a log disk.
 *
 * Node-safe: relative imports only, no import.meta.
 */
import { z } from 'zod'
import { WORKWISE_REGIONS } from '../../config/azureRegions'
import {
  BLOCKED_NSG_PORTS,
  CUSTOMER_NAME_MAX,
  CUSTOMER_NAME_MIN,
  DB_ENGINE_VERSION,
  DISK_SIZE_MAX_GB,
  DISK_SIZE_MIN_GB,
  EXISTING_NETWORK_DEDICATED_MESSAGE,
  MAX_DISKS,
  MAX_EXTRA_TAGS,
  MAX_NSG_RULES,
  MODEL_MISMATCH_MESSAGE,
  NETWORK_CIDR_REQUIRED_MESSAGE,
  NETWORK_CIDR_SAAS_MESSAGE,
  OPEN_NSG_SOURCES,
  RESERVED_TAG_KEYS,
  SLUG_MAX,
  SLUG_MIN,
  dedicatedSizeMessage,
} from '../../config/workwise'
import type { DeploymentModel, DiskType, WorkwiseEnvironment } from '../../types/workwise'
import { type CidrField, cidrPairProblems } from './cidr'
import {
  SQL_LOGIN_FORMAT_MESSAGE,
  SQL_LOGIN_RESERVED_MESSAGE,
  ZONE_REQUIRED_MESSAGE,
  dbCountsInRange,
  dbDiskCount,
  isReservedSqlLogin,
  sqlLoginProblem,
  totalDisksMessage,
  zoneOnlyRoles,
} from './dbStorage'
import { validateAppVmName, validateDbVmName, validateGatewayVmName } from './naming'
import {
  CUSTOMER_NUMBER_REGEX,
  EMAIL_REGEX,
  KV_REF_REGEX,
  REGION_CODE_REGEX,
  SLUG_REGEX,
  SQL_LOGIN_REGEX,
  UUID_V4_REGEX,
} from './patterns'

// ─── Primitives ──────────────────────────────────────────────────────────────

const SKU = z.enum(['S', 'M', 'L', 'XL'])
const DISK_TYPE = z.enum([
  'Standard_LRS', 'StandardSSD_LRS', 'StandardSSD_ZRS', 'Premium_LRS',
  'Premium_ZRS', 'PremiumV2_LRS', 'UltraSSD_LRS',
] as [DiskType, ...DiskType[]])
const ENVIRONMENT = z.enum(['dev', 'stg', 'production'])
const MODEL = z.enum(['saas-hybrid', 'dedicated'], {
  errorMap: () => ({ message: 'Choose SaaS-Hybrid or Dedicated' }),
})
const CICD = z.enum(['github_actions', 'azure_devops'])
/** SQL Server 2022 Standard on Windows Server 2022 is the only image; 2019 is no longer valid. */
const ENGINE_VERSION = z.literal(DB_ENGINE_VERSION)
const SQL_AUTHENTICATION = z.enum(['windows', 'sql'])
const STORAGE_WORKLOAD = z.enum(['general', 'oltp', 'dw'])
const DB_DISK_ROLE = z.enum(['data', 'log', 'tempdb'])
const ZONE_CHOICE = z.enum(['', '1', '2', '3'])
const ZONE_VALUE = z.enum(['1', '2', '3']).nullable()

const KV_MESSAGE = 'Use a Key Vault reference: kv://<vault>/<secret> (lowercase vault name).'

/** Form inputs give '' or NaN for an empty number box: treat both as missing. */
function emptyToUndefined(value: unknown): unknown {
  if (value === '' || value === null) return undefined
  if (typeof value === 'number' && Number.isNaN(value)) return undefined
  return value
}

function intField(label: string, min: number, max: number, optional = false) {
  const base = z
    .number({ required_error: `${label} is required`, invalid_type_error: `${label} must be a number` })
    .int(`${label} must be a whole number`)
    .min(min, `${label} must be at least ${min}`)
    .max(max, `${label} must be at most ${max}`)
  return z.preprocess(emptyToUndefined, optional ? base.optional() : base)
}

const requiredText = (label: string, max = 253) =>
  z.string().trim().min(1, `${label} is required`).max(max, `${label} is too long`)

const kvRef = (label: string) =>
  z.string().trim().min(1, `${label} is required`).regex(KV_REF_REGEX, KV_MESSAGE)

const sharedServerId = (label: string) =>
  z.number({ required_error: `Select a shared ${label} server`, invalid_type_error: `Select a shared ${label} server` })
    .int()
    .positive(`Select a shared ${label} server`)

function isOpenNsgSource(source: string): boolean {
  return OPEN_NSG_SOURCES.includes(source.trim().toLowerCase())
}

function nsgRuleProblem(port: number, source: string): string | null {
  if (BLOCKED_NSG_PORTS.includes(port) && isOpenNsgSource(source)) {
    return `Port ${port} cannot be open to the internet. Use a specific address range.`
  }
  return null
}

// ─── Form schema ─────────────────────────────────────────────────────────────

export interface SchemaContext {
  env: WorkwiseEnvironment
  /** Current value of customer.number (may be incomplete while typing). */
  customerNumber: string
  /** Current deployment model: picks the shared or the dedicated DB/gateway variant. */
  model: DeploymentModel
  /** The Application (ERP) step is skipped (infrastructure only): `app` is not validated. Default false. */
  skipApplication?: boolean
}

export function buildFormSchema(ctx: SchemaContext) {
  const vmName = (check: (name: string) => string | null) =>
    z.string().superRefine((value, c) => {
      const message = check(value)
      if (message) c.addIssue({ code: z.ZodIssueCode.custom, message })
    })

  const customer = z
    .object({
      name: z
        .string()
        .trim()
        .min(CUSTOMER_NAME_MIN, `Customer name must be at least ${CUSTOMER_NAME_MIN} characters`)
        .max(CUSTOMER_NAME_MAX, `Customer name must be at most ${CUSTOMER_NAME_MAX} characters`),
      number: z
        .string()
        .regex(CUSTOMER_NUMBER_REGEX, 'Enter exactly 3 digits (001 to 999)')
        .refine((v) => v !== '000', 'Customer number must be 001 to 999'),
      slug: z
        .string()
        .min(SLUG_MIN, `Slug must be at least ${SLUG_MIN} characters`)
        .max(SLUG_MAX, `Slug must be at most ${SLUG_MAX} characters`)
        .regex(SLUG_REGEX, 'Use lowercase letters, digits and single hyphens'),
      confirmNumber: z.string(),
    })
    .superRefine((value, c) => {
      if (ctx.env === 'production' && value.confirmNumber.trim() !== value.number) {
        c.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['confirmNumber'],
          message: 'Type the customer number to confirm this production deployment.',
        })
      }
    })

  const disk = z.object({
    sizeGb: intField('Disk size', DISK_SIZE_MIN_GB, DISK_SIZE_MAX_GB),
    type: DISK_TYPE,
  })
  /** The "Data Disks + Add Disk" list of the app server and the gateway: same limits for both (R3-G). */
  const disks = (min: 0 | 1) =>
    z
      .array(disk)
      .min(min, 'Add at least one data disk.')
      .max(MAX_DISKS, `At most ${MAX_DISKS} data disks.`)

  /** One row of "SQL Server storage": `count` disks of `sizeGb` GB and `type`. */
  const dbDiskGroup = {
    type: DISK_TYPE,
    sizeGb: intField('Disk size', DISK_SIZE_MIN_GB, DISK_SIZE_MAX_GB),
    count: intField('Number of disks', 1, MAX_DISKS),
  }
  // TempDB on the local SSD has no disks: its (remembered) type, size and count are not validated.
  const dbStorage = z.object({
    data: z.object(dbDiskGroup),
    log: z.object(dbDiskGroup),
    tempdb: z.discriminatedUnion('location', [
      z.object({ location: z.literal('local') }),
      z.object({ location: z.literal('disks'), ...dbDiskGroup }),
    ]),
  })

  const appServer = z.object({
    sku: SKU,
    vmName: vmName((n) => validateAppVmName(n, ctx.env, ctx.customerNumber)),
    os: requiredText('Operating system', 64),
    osDiskType: DISK_TYPE,
    disks: disks(0),
    zone: ZONE_CHOICE,
  })

  const dbShared = z.object({
    sharedServerId: sharedServerId('database'),
  })
  // No sku: a dedicated DB / gateway takes the step-2 size (R3-G).
  const dbDedicated = z
    .object({
      vmName: vmName((n) => validateDbVmName(n, ctx.env, ctx.customerNumber)),
      sqlAuthentication: SQL_AUTHENTICATION,
      sqlLogin: z.string(),
      storageWorkload: STORAGE_WORKLOAD,
      storage: dbStorage,
      zone: ZONE_CHOICE,
    })
    .superRefine((db, c) => {
      const issue = (path: Array<string | number>, message: string) =>
        c.addIssue({ code: z.ZodIssueCode.custom, path, message })
      // 'sql' (SQL Server authentication) needs a login; 'windows' sends none, so a remembered one is not checked.
      if (db.sqlAuthentication === 'sql') {
        const problem = sqlLoginProblem(db.sqlLogin)
        if (problem) issue(['sqlLogin'], problem)
      }
      // At most 16 disks in the three rows together (judged once every row has a count of 1 to 16).
      if (dbCountsInRange(db.storage)) {
        const total = dbDiskCount(db.storage)
        if (total > MAX_DISKS) issue(['storage', 'total'], totalDisksMessage(total))
      }
      // Premium SSD v2 and Ultra disks exist only in a zone: said on the zone and on each row that uses one.
      const zoneOnly = zoneOnlyRoles(db.storage)
      if (db.zone === '' && zoneOnly.length > 0) {
        issue(['zone'], ZONE_REQUIRED_MESSAGE)
        for (const role of zoneOnly) issue(['storage', role, 'type'], ZONE_REQUIRED_MESSAGE)
      }
    })

  const nsgRule = z
    .object({
      name: requiredText('Rule name', 80),
      port: intField('Port', 1, 65535),
      source: requiredText('Source', 64),
    })
    .superRefine((rule, c) => {
      const problem = nsgRuleProblem(rule.port as number, rule.source)
      if (problem) c.addIssue({ code: z.ZodIssueCode.custom, path: ['source'], message: problem })
    })

  // Revision 8: no host name or TLS certificate in either mode.
  const gwShared = z.object({
    sharedServerId: sharedServerId('gateway'),
  })
  const gwDedicated = z.object({
    vmName: vmName((n) => validateGatewayVmName(n, ctx.env)),
    publicIp: z.boolean(),
    disks: disks(0),
    nsgRules: z.array(nsgRule).max(MAX_NSG_RULES, `At most ${MAX_NSG_RULES} rules`),
    zone: ZONE_CHOICE,
  })
  const dedicated = ctx.model === 'dedicated'

  // R4-A: Dedicated types its VNet and subnet ranges (checked as they are sent: trimmed); SaaS-Hybrid may
  // pick an existing network.
  const networkDedicated = z.object({ vnetCidr: z.string(), subnetCidr: z.string() }).superRefine((n, c) => {
    for (const [key, message] of Object.entries(cidrPairProblems(n.vnetCidr.trim(), n.subnetCidr.trim()))) {
      c.addIssue({ code: z.ZodIssueCode.custom, path: [key], message })
    }
  })
  const networkShared = z.object({ existingNetworkId: z.number().int().positive().optional() })

  const app = z.object({
    version: requiredText('Version', 64),
    tenantName: z.string().trim().min(2, 'Tenant name must be at least 2 characters').max(100, 'Tenant name must be at most 100 characters'),
    licenseKeyRef: kvRef('License key reference'),
    smtp: z.object({
      host: requiredText('SMTP host'),
      port: intField('SMTP port', 1, 65535),
      username: z.string().max(256, 'Username must be at most 256 characters'),
      passwordRef: kvRef('SMTP password reference'),
      fromAddress: z.string().trim().regex(EMAIL_REGEX, 'Enter a valid e-mail address'),
      useTls: z.boolean(),
    }),
    dbConnectionRef: kvRef('Database connection reference'),
  })

  const tags = z
    .object({
      owner: requiredText('Owner', 254),
      extra: z.array(z.object({ key: z.string(), value: z.string().max(256, 'Value is too long') }))
        .max(MAX_EXTRA_TAGS, `At most ${MAX_EXTRA_TAGS} extra tags`),
    })
    .superRefine((value, c) => {
      const seen = new Set<string>()
      value.extra.forEach((tag, i) => {
        const key = tag.key.trim()
        const lower = key.toLowerCase()
        let message: string | null = null
        if (!key) message = 'Tag key is required'
        else if (key.length > 64) message = 'Tag key must be at most 64 characters'
        else if (RESERVED_TAG_KEYS.includes(lower.replace(/[-_\s]/g, ''))) message = 'This key is reserved by the portal'
        else if (seen.has(lower)) message = 'Duplicate tag key'
        seen.add(lower)
        if (message) c.addIssue({ code: z.ZodIssueCode.custom, path: ['extra', i, 'key'], message })
      })
    })

  return z.object({
    customer,
    environment: ENVIRONMENT,
    region: z.string().refine((v) => WORKWISE_REGIONS.includes(v), 'Select a region'),
    cicd: CICD,
    deploymentModel: MODEL,
    appServer,
    // The other variant's inputs stay in memory but are not validated.
    dbServer: dedicated ? dbDedicated : dbShared,
    gatewayServer: dedicated ? gwDedicated : gwShared,
    network: dedicated ? networkDedicated : networkShared,
    skipApplication: z.boolean(),
    // Skipped: whatever is typed in step 4 stays in memory but is neither validated nor sent.
    app: ctx.skipApplication ? z.unknown() : app,
    tags,
  })
}

// ─── Strict outgoing payload ─────────────────────────────────────────────────

const payloadDisk = z
  .object({ sizeGb: z.number().int().min(DISK_SIZE_MIN_GB).max(DISK_SIZE_MAX_GB), type: DISK_TYPE })
  .strict()
const payloadDisks = (min: 0 | 1) => z.array(payloadDisk).min(min).max(MAX_DISKS)
/** A disk of a dedicated DB: the role it has in SQL Server's storage. */
const payloadDbDisk = z
  .object({ sizeGb: z.number().int().min(DISK_SIZE_MIN_GB).max(DISK_SIZE_MAX_GB), type: DISK_TYPE, role: DB_DISK_ROLE })
  .strict()
const payloadNsgRule = z
  .object({ name: z.string().min(1).max(80), port: z.number().int().min(1).max(65535), source: z.string().min(1) })
  .strict()

const payloadDbShared = z
  .object({
    mode: z.literal('shared'),
    sharedServerId: z.number().int().positive(),
    databaseName: z.string().min(1).max(128).optional(),
  })
  .strict()

const payloadDbDedicated = z
  .object({
    mode: z.literal('dedicated'),
    sku: SKU,
    vmName: z.string(),
    engine: z.literal('sqlserver'),
    engineVersion: ENGINE_VERSION,
    databaseName: z.string().min(1).max(128).optional(),
    // Like the backend, both default when absent; the wizard always sends them.
    sqlAuthentication: SQL_AUTHENTICATION.default('windows'),
    sqlLogin: z.string().regex(SQL_LOGIN_REGEX, SQL_LOGIN_FORMAT_MESSAGE).optional(),
    storageWorkload: STORAGE_WORKLOAD.default('general'),
    disks: z.array(payloadDbDisk).min(1).max(MAX_DISKS),
    zone: ZONE_VALUE,
  })
  .strict()

// Revision 8: `hostname` and `tlsCertRef` are retired; strict() refuses them like any unknown key.
const payloadGwShared = z
  .object({
    mode: z.literal('shared'),
    sharedServerId: z.number().int().positive(),
  })
  .strict()

const payloadGwDedicated = z
  .object({
    mode: z.literal('dedicated'),
    sku: SKU,
    vmName: z.string(),
    publicIp: z.boolean(),
    disks: payloadDisks(0),
    nsgRules: z.array(payloadNsgRule).max(MAX_NSG_RULES),
    zone: ZONE_VALUE,
  })
  .strict()

/** R4-A: one strict object; which keys a model may carry is checked in the refinement below. */
const payloadNetwork = z
  .object({
    vnetCidr: z.string().optional(),
    subnetCidr: z.string().optional(),
    existingNetworkId: z.number().int().positive().optional(),
  })
  .strict()

type NetworkIssue = (path: Array<string | number>, message: string) => void

/** Dedicated: both ranges, valid, the subnet inside the VNet, no existing network. SaaS-Hybrid: no ranges. */
function checkPayloadNetwork(dedicated: boolean, network: z.infer<typeof payloadNetwork> | undefined, issue: NetworkIssue): void {
  const fields: CidrField[] = ['vnetCidr', 'subnetCidr']
  if (!dedicated) {
    for (const key of fields) if (network?.[key] !== undefined) issue(['network', key], NETWORK_CIDR_SAAS_MESSAGE)
    return
  }
  for (const key of fields) if (network?.[key] === undefined) issue(['network', key], NETWORK_CIDR_REQUIRED_MESSAGE)
  if (network?.existingNetworkId !== undefined) issue(['network', 'existingNetworkId'], EXISTING_NETWORK_DEDICATED_MESSAGE)
  const problems = cidrPairProblems(network?.vnetCidr ?? '', network?.subnetCidr ?? '')
  for (const key of fields) {
    const message = problems[key]
    if (message && network?.[key] !== undefined) issue(['network', key], message)
  }
}

/** A dedicated DB's SQL login and storage rules, with the backend's messages (the field rules already passed). */
function checkPayloadSqlOptions(
  db: { sqlAuthentication: 'windows' | 'sql'; sqlLogin?: string; disks: Array<{ role: 'data' | 'log' | 'tempdb' }> },
  issue: NetworkIssue,
): void {
  if (db.sqlAuthentication === 'sql') {
    if (db.sqlLogin === undefined) issue(['dbServer', 'sqlLogin'], 'Required for SQL Server authentication.')
    // like the backend: the reserved names are judged once the format rule has passed
    else if (SQL_LOGIN_REGEX.test(db.sqlLogin) && isReservedSqlLogin(db.sqlLogin)) {
      issue(['dbServer', 'sqlLogin'], SQL_LOGIN_RESERVED_MESSAGE)
    }
  } else if (db.sqlLogin !== undefined) {
    issue(['dbServer', 'sqlLogin'], 'Only allowed with SQL Server authentication.')
  }
  const roles = new Set(db.disks.map((disk) => disk.role))
  if (!roles.has('data')) issue(['dbServer', 'disks'], 'Add at least one data disk.')
  if (!roles.has('log')) issue(['dbServer', 'disks'], 'Add at least one log disk.')
}

export const payloadSchema = z
  .object({
    customer: z
      .object({
        name: z.string().trim().min(CUSTOMER_NAME_MIN).max(CUSTOMER_NAME_MAX),
        number: z.string().regex(CUSTOMER_NUMBER_REGEX).refine((v) => v !== '000'),
        slug: z.string().min(SLUG_MIN).max(SLUG_MAX).regex(SLUG_REGEX),
      })
      .strict(),
    environment: ENVIRONMENT,
    region: z.string().regex(REGION_CODE_REGEX),
    cloudProvider: z.literal('azure'),
    cicd: CICD,
    appServer: z
      .object({
        sku: SKU,
        vmName: z.string(),
        os: z.string().min(1),
        osDiskType: DISK_TYPE,
        disks: payloadDisks(0),
        zone: ZONE_VALUE,
      })
      .strict(),
    dbServer: z.discriminatedUnion('mode', [payloadDbShared, payloadDbDedicated]),
    gatewayServer: z.discriminatedUnion('mode', [payloadGwShared, payloadGwDedicated]),
    network: payloadNetwork.optional(),
    // Optional like the backend's: the Application (ERP) step can be skipped (infrastructure only).
    app: z
      .object({
        version: z.string().min(1),
        tenantName: z.string().min(2).max(100),
        licenseKeyRef: z.string().regex(KV_REF_REGEX),
        smtp: z
          .object({
            host: z.string().min(1),
            port: z.number().int().min(1).max(65535),
            username: z.string().max(256),
            passwordRef: z.string().regex(KV_REF_REGEX),
            fromAddress: z.string().regex(EMAIL_REGEX),
            useTls: z.boolean(),
          })
          .strict(),
        dbConnectionRef: z.string().regex(KV_REF_REGEX),
      })
      .strict()
      .optional(),
    tags: z
      .object({
        owner: z.string().min(1).max(254),
        extra: z.record(z.string().min(1).max(64), z.string().max(256)).optional(),
      })
      .strict(),
    estimatedMonthlyCost: z.number().min(0),
    correlationId: z.string().regex(UUID_V4_REGEX),
    approvalRequired: z.boolean().optional(),
    approvalNotes: z.string().max(2000).optional(),
  })
  .strict()
  .superRefine((p, c) => {
    const issue = (path: Array<string | number>, message: string) =>
      c.addIssue({ code: z.ZodIssueCode.custom, path, message })

    // Revision 3: SaaS-Hybrid (both shared) or Dedicated (both dedicated); never mixed.
    if (p.dbServer.mode !== p.gatewayServer.mode) issue(['gatewayServer', 'mode'], MODEL_MISMATCH_MESSAGE)
    else checkPayloadNetwork(p.dbServer.mode === 'dedicated', p.network, issue)
    const appName = validateAppVmName(p.appServer.vmName, p.environment, p.customer.number)
    if (appName) issue(['appServer', 'vmName'], appName)
    if (p.dbServer.mode === 'dedicated') {
      const message = validateDbVmName(p.dbServer.vmName, p.environment, p.customer.number)
      if (message) issue(['dbServer', 'vmName'], message)
      // R3-G: a dedicated server takes the size chosen in Configure Sizes.
      if (p.dbServer.sku !== p.appServer.sku) issue(['dbServer', 'sku'], dedicatedSizeMessage(p.appServer.sku))
      checkPayloadSqlOptions(p.dbServer, issue)
    }
    if (p.gatewayServer.mode === 'dedicated') {
      const message = validateGatewayVmName(p.gatewayServer.vmName, p.environment)
      if (message) issue(['gatewayServer', 'vmName'], message)
      if (p.gatewayServer.sku !== p.appServer.sku) issue(['gatewayServer', 'sku'], dedicatedSizeMessage(p.appServer.sku))
      p.gatewayServer.nsgRules.forEach((rule, i) => {
        const problem = nsgRuleProblem(rule.port, rule.source)
        if (problem) issue(['gatewayServer', 'nsgRules', i, 'source'], problem)
      })
    }
  })

export type WorkwisePayloadParsed = z.infer<typeof payloadSchema>
